import { useMemo, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import { useAdvancedModulesHidden } from '../lib/ui-prefs';
import { date, money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import { toast } from '../lib/toast';
import { saveBlob } from '../lib/einvoice-api';
import {
  CLOSING_ISSUES,
  downloadAccountantPackage,
  STATUS_LABELS,
  useAccounts,
  useCloseYear,
  useClosingPreview,
  useClosingReadiness,
  useCreateJournalEntry,
  useFiscalYears,
  useInventories,
  useInventoryPreview,
  useJournalEntries,
  useJournals,
  useOpenFiscalYear,
  useOpeningBalance,
  usePostJournalEntry,
  useReopenYear,
  useReverseJournalEntry,
  useTakeInventory,
  unpostedTotal,
  useUnposted,
  type InventoryLine,
  type JournalEntry,
  type JournalEntryStatus,
} from '../lib/accounting-api';
import {
  AdvancedSection,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * DEFTER — yevmiye fişleri ve hesap planı.
 *
 * ⚠ "GERİ AÇ" DÜĞMESİ YOK ve olmayacak. Atılmış fiş düzeltilmez, TERS KAYITLA
 * giderilir; ikisi de defterde kalır. Alış ekranında "Taslağa geri aç" vardır
 * çünkü orada kayıt tedarikçinin belgesinin bir KOPYASIDIR — burada kayıt
 * belgenin KENDİSİDİR.
 *
 * ⚠ DENGE PANELDE DE HESAPLANIYOR ama kapı SUNUCUDA. Buradaki toplam, "Ata"
 * düğmesine basmadan önce farkı GÖSTERMEK içindir; reddi sunucu verir
 * (`entry-balance.ts`) ve DB tetikleyicisi ayrıca zorlar.
 */

const PAGE_SIZE = 25;

interface DraftLine {
  accountCode: string;
  debit: string;
  credit: string;
  description: string;
}

function emptyLine(): DraftLine {
  return { accountCode: '', debit: '', credit: '', description: '' };
}

/**
 * Tam sayı kuruştan METİN — `cents / 100` YAZILMAZ.
 *
 * ⚠ `money.ts` doktrini panelde de geçerli: bölme bir float üretir ve
 * gösterim katmanı olsa bile, o floatı bir gün birinin isteğe koyması
 * yalnızca bir kopyala-yapıştır uzaklıkta. Metin tam sayılardan kuruluyor.
 */
function centsToText(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return sign + String(Math.floor(abs / 100)) + '.' + String(abs % 100).padStart(2, '0');
}

/** Girilen metni tam sayı kuruşa çevirir; okunamayan değer için NaN. */
function centsOf(text: string): number {
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  if (!/^\d{1,12}(\.\d{1,2})?$/.test(trimmed)) return Number.NaN;
  const [whole = '0', frac = ''] = trimmed.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

export function AccountingPage() {
  const { user } = useAuth();
  const readOnly = isReadOnly(user);
  const advancedHidden = useAdvancedModulesHidden();

  const [status, setStatus] = useState<JournalEntryStatus | 'all'>('all');
  const [journalCode, setJournalCode] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<'MANUAL' | 'OPENING' | null>(null);

  const journals = useJournals();
  const years = useFiscalYears();
  const openYear = useOpenFiscalYear();
  const [yearError, setYearError] = useState<string | null>(null);

  const list = useJournalEntries({
    page,
    pageSize: PAGE_SIZE,
    status: status === 'all' ? undefined : status,
    journalCode: journalCode || undefined,
    q: q.trim() || undefined,
  });

  return (
    <>
      <PageHeader
        title="Defter"
        description="Yevmiye fişleri. Atılmış bir fiş DEĞİŞTİRİLEMEZ ve geri açılamaz — yanlışsa ters kayıt atılır ve ikisi de defterde kalır."
        actions={
          readOnly ? null : (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setOpen((v) => (v === 'OPENING' ? null : 'OPENING'))}
              >
                {open === 'OPENING' ? 'Vazgeç' : 'Açılış fişi'}
              </Button>
              <Button onClick={() => setOpen((v) => (v === 'MANUAL' ? null : 'MANUAL'))}>
                {open === 'MANUAL' ? 'Vazgeç' : 'Yeni fiş'}
              </Button>
            </div>
          )
        }
      />

      <Card className="mt-4">
        <CardHeader
          title="Mali yıllar"
          description="Fiş atmadan önce yılın açık olması gerekir. Kapatılmış yıla kayıt atılamaz."
        />
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          {years.isPending ? (
            <Spinner />
          ) : (
            (years.data ?? []).map((y) => (
              <Badge key={y.year} tone={y.status === 'OPEN' ? 'ok' : 'warn'}>
                {y.year} · {y.status === 'OPEN' ? 'açık' : 'kapalı'}
              </Badge>
            ))
          )}
          {(years.data ?? []).length === 0 && !years.isPending && (
            <span className="text-xs text-[var(--c-ink-soft)]">Henüz mali yıl açılmamış.</span>
          )}
          {!readOnly && (
            <Button
              variant="ghost"
              disabled={openYear.isPending}
              onClick={() => {
                setYearError(null);
                const now = new Date().getFullYear();
                openYear.mutateAsync(now).catch((e: unknown) => setYearError(errorMessage(e)));
              }}
            >
              {new Date().getFullYear()} yılını aç
            </Button>
          )}
        </div>
        {yearError && <p className="px-4 pb-3 text-xs text-[var(--c-danger)]">{yearError}</p>}
      </Card>

      <UnpostedCard />

      {/*
        ⚠ İKİSİ DE M11 DENETİMİNDE "yasal olarak zorunlu değil" bulundu
        (bijberoep personne physique için WER/CDE yalnız üç defter + yıllık
        temel envanter ister). Silinmedi — mükellef "ilerde kullanabiliriz"
        dedi; yalnız günlük akıştan uzaklaştırıldı.
      */}
      <AdvancedSection hidden={advancedHidden}>
        <YearEndCard readOnly={readOnly} />
      </AdvancedSection>

      <AdvancedSection hidden={advancedHidden}>
        <InventoryCard readOnly={readOnly} />
      </AdvancedSection>

      {open !== null && !readOnly && <EntryForm mode={open} onDone={() => setOpen(null)} />}

      <Card className="mt-4">
        <CardHeader title="Fişler" />
        <div className="flex flex-wrap gap-2 px-4 py-3">
          <Select
            label="Durum"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as JournalEntryStatus | 'all');
              setPage(1);
            }}
          >
            <option value="all">Hepsi</option>
            <option value="DRAFT">Taslak</option>
            <option value="POSTED">Atıldı</option>
            <option value="REVERSED">Ters çevrildi</option>
          </Select>
          <Select
            label="Dergi"
            value={journalCode}
            onChange={(e) => {
              setJournalCode(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Hepsi</option>
            {(journals.data ?? []).map((j) => (
              <option key={j.code} value={j.code}>
                {j.code} — {j.name.tr}
              </option>
            ))}
          </Select>
          <Input
            label="Ara"
            value={q}
            placeholder="Açıklama ya da fiş no"
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {list.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : list.isError ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Fiş yok" description="Henüz yevmiye fişi yazılmamış." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Fiş no</Th>
                <Th>Tarih</Th>
                <Th>Dergi</Th>
                <Th>Açıklama</Th>
                <Th>Kaynak</Th>
                <Th>Durum</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((e) => (
                <EntryRow key={e.id} entry={e} readOnly={readOnly} />
              ))}
            </tbody>
          </Table>
        )}

        {(list.data?.total ?? 0) > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 text-xs text-[var(--c-ink-soft)]">
            <span>
              {list.data?.total} fiş · sayfa {page}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                Önceki
              </Button>
              <Button
                variant="ghost"
                disabled={page * PAGE_SIZE >= (list.data?.total ?? 0)}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

function EntryRow({ entry, readOnly }: { entry: JournalEntry; readOnly: boolean }) {
  const post = usePostJournalEntry();
  const reverse = useReverseJournalEntry();
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <>
      <tr>
        <Td className="font-mono text-xs">{entry.entryNumber ?? '—'}</Td>
        <Td>{date(entry.entryDate)}</Td>
        <Td>{entry.journalCode}</Td>
        <Td>{entry.description}</Td>
        <Td className="text-xs text-[var(--c-ink-soft)]">{entry.sourceType}</Td>
        <Td>
          <Badge
            tone={
              entry.status === 'POSTED' ? 'ok' : entry.status === 'REVERSED' ? 'warn' : 'neutral'
            }
          >
            {STATUS_LABELS[entry.status]}
          </Badge>
        </Td>
        <Td>
          {!readOnly && (
            <div className="flex gap-1.5">
              {entry.status === 'DRAFT' && (
                <Button
                  variant="ghost"
                  disabled={post.isPending}
                  onClick={() => void act(() => post.mutateAsync(entry.id))}
                >
                  Ata
                </Button>
              )}
              {/*
                ⚠ "GERİ AÇ" DÜĞMESİ YOK. Atılmış fişi düzeltmenin tek yolu ters
                kayıttır ve o da YENİ bir fiş doğurur.
              */}
              {entry.status === 'POSTED' && (
                <Button
                  variant="ghost"
                  disabled={reverse.isPending}
                  onClick={() =>
                    void act(() =>
                      reverse.mutateAsync({
                        id: entry.id,
                        entryDate: new Date().toISOString().slice(0, 10),
                      }),
                    )
                  }
                >
                  Ters kayıt
                </Button>
              )}
            </div>
          )}
        </Td>
      </tr>
      {error && (
        <tr>
          <Td colSpan={7}>
            <p className="text-xs text-[var(--c-danger)]">{error}</p>
          </Td>
        </tr>
      )}
    </>
  );
}

/**
 * Yevmiye fişi formu — iki modda.
 *
 * ⚠ AÇILIŞ FİŞİ AYNI FORMU KULLANIYOR ama ayrı bir uca gidiyor. Sebep
 * elle fiş yolunun açılışın TEK OLMA kuralını ifade edememesi: açılış bir
 * YILA bağlıdır ve o yılda bir tane olmalıdır. İkinci bir açılış tüm
 * bakiyeleri iki katına çıkarır ve mizan yine DENK görünürdü.
 *
 * ⚠ AÇILIŞTA TARİH SORULMUYOR: yılın ilk günüdür ve sunucu onu koyar.
 */
function EntryForm({ onDone, mode }: { onDone: () => void; mode: 'MANUAL' | 'OPENING' }) {
  const opening = mode === 'OPENING';
  const openingBalance = useOpeningBalance();
  const years = useFiscalYears();
  const [fiscalYear, setFiscalYear] = useState<number | null>(null);
  const accounts = useAccounts();
  const journals = useJournals();
  const create = useCreateJournalEntry();
  const post = usePostJournalEntry();

  const [journalCode, setJournalCode] = useState('DIV');
  const [entryDate, setEntryDate] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(), emptyLine()]);
  const [error, setError] = useState<string | null>(null);

  const setLine = (i: number, patch: Partial<DraftLine>): void => {
    setLines((prev) => prev.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  };

  /*
   * ⚠ TOPLAM PANELDE GÖSTERİLİR AMA KARARI SUNUCU VERİR. Buradaki hesap
   * operatörün farkı ANINDA görmesi içindir; "Ata" reddi `entry-balance.ts`ten
   * gelir ve DB tetikleyicisi ayrıca zorlar. İki yerde hesaplanan bir kural
   * değil — biri gösterim, öteki kapı.
   */
  const totals = useMemo(() => {
    let d = 0;
    let c = 0;
    let bad = false;
    for (const l of lines) {
      const ld = centsOf(l.debit);
      const lc = centsOf(l.credit);
      if (Number.isNaN(ld) || Number.isNaN(lc)) bad = true;
      else {
        d += ld;
        c += lc;
      }
    }
    return { d, c, bad, diff: d - c };
  }, [lines]);

  const submit = async (postToo: boolean): Promise<void> => {
    setError(null);
    try {
      const body = {
        journalCode,
        entryDate,
        description,
        lines: lines
          .filter((l) => l.accountCode.trim() !== '')
          .map((l) => ({
            accountCode: l.accountCode.trim(),
            debit: l.debit.trim() || '0',
            credit: l.credit.trim() || '0',
            ...(l.description.trim() ? { description: l.description.trim() } : {}),
          })),
      };
      if (opening) {
        const year = fiscalYear ?? years.data?.[0]?.year ?? null;
        if (year === null) {
          setError('Önce bir mali yıl açın.');
          return;
        }
        await openingBalance.mutateAsync({ fiscalYear: year, lines: body.lines });
        onDone();
        return;
      }
      const saved = await create.mutateAsync(body);
      if (postToo) await post.mutateAsync(saved.id);
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader
        title={opening ? 'Açılış fişi' : 'Yeni yevmiye fişi'}
        description={
          opening
            ? 'Mali yılın başlangıç bakiyeleri. Bir yılda EN ÇOK BİR açılış olabilir; tarih yılın ilk günüdür ve sorulmaz.'
            : 'Her satırın TEK tarafı dolu olur: ya borç ya alacak. İkisi de dolu bir satır aslında iki satırdır.'
        }
      />
      {opening && (
        <div className="px-4 pt-3">
          <Select
            label="Mali yıl"
            value={String(fiscalYear ?? years.data?.[0]?.year ?? '')}
            onChange={(e) => setFiscalYear(Number(e.target.value))}
          >
            {(years.data ?? []).map((y) => (
              <option key={y.year} value={y.year}>
                {y.year}
              </option>
            ))}
          </Select>
        </div>
      )}
      <div className={opening ? 'hidden' : 'grid gap-3 px-4 py-3 sm:grid-cols-3'}>
        <Select label="Dergi" value={journalCode} onChange={(e) => setJournalCode(e.target.value)}>
          {(journals.data ?? []).map((j) => (
            <option key={j.code} value={j.code}>
              {j.code} — {j.name.tr}
            </option>
          ))}
        </Select>
        <Input
          label="Tarih"
          type="date"
          value={entryDate}
          onChange={(e) => setEntryDate(e.target.value)}
        />
        <Input
          label="Açıklama"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="px-4 pb-2">
        <Table>
          <thead>
            <tr>
              <Th>Hesap</Th>
              <Th>Açıklama</Th>
              <Th align="right">Borç</Th>
              <Th align="right">Alacak</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <Td>
                  <Select
                    value={l.accountCode}
                    onChange={(e) => setLine(i, { accountCode: e.target.value })}
                  >
                    <option value="">— seçin —</option>
                    {(accounts.data ?? []).map((a) => (
                      <option key={a.code} value={a.code}>
                        {a.code} — {a.name.tr}
                      </option>
                    ))}
                  </Select>
                </Td>
                <Td>
                  <Input
                    value={l.description}
                    onChange={(e) => setLine(i, { description: e.target.value })}
                  />
                </Td>
                <Td align="right">
                  <Input
                    value={l.debit}
                    placeholder="0.00"
                    onChange={(e) => setLine(i, { debit: e.target.value, credit: '' })}
                  />
                </Td>
                <Td align="right">
                  <Input
                    value={l.credit}
                    placeholder="0.00"
                    onChange={(e) => setLine(i, { credit: e.target.value, debit: '' })}
                  />
                </Td>
                <Td>
                  <Button
                    variant="ghost"
                    disabled={lines.length <= 2}
                    onClick={() => setLines((prev) => prev.filter((_, k) => k !== i))}
                  >
                    Sil
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="mt-2 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setLines((prev) => [...prev, emptyLine()])}>
            Satır ekle
          </Button>
          <p className="text-xs">
            Borç {money(centsToText(totals.d))} · Alacak {money(centsToText(totals.c))}{' '}
            {totals.bad ? (
              <span className="text-[var(--c-danger)]">· tutar okunamadı</span>
            ) : totals.diff === 0 ? (
              <span className="text-[var(--c-ok)]">· dengeli</span>
            ) : (
              <span className="text-[var(--c-danger)]">
                · fark {money(centsToText(Math.abs(totals.diff)))}
              </span>
            )}
          </p>
        </div>
      </div>

      {error && <p className="px-4 pb-2 text-xs text-[var(--c-danger)]">{error}</p>}

      <div className="flex justify-end gap-2 border-t border-[var(--c-border)] px-4 py-3">
        <Button variant="ghost" onClick={onDone}>
          Vazgeç
        </Button>
        {!opening && (
          <Button variant="ghost" disabled={create.isPending} onClick={() => void submit(false)}>
            Taslak kaydet
          </Button>
        )}
        {/*
          ⚠ "Kaydet ve at" tek düğme ama İKİ istek: şema doğrudan POSTED fiş
          yaratmaya izin vermiyor (satır tetikleyicisi INSERT'i de kapsıyor).
          Sunucu tarafında ikisi de kendi transaction'ında.
        */}
        <Button
          disabled={create.isPending || post.isPending || openingBalance.isPending}
          onClick={() => void submit(true)}
        >
          {opening ? 'Açılışı kaydet' : 'Kaydet ve at'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Deftere girmemiş belgeler.
 *
 * ⚠ BU KART BİR TELAFİDİR. Kayıt, belge işleminden AYRI bir adımdır (fatura
 * kesme transaction'ı numara sayacının kilidini tutuyor; oraya ikinci bir
 * sayaç sokmak deadlock riski açardı). Ayrı olduğu için UNUTULABİLİR — ve
 * unutulan bir belge mizanda hiç görünmez. Sayının panoda durması o riskin
 * karşılığı; kayıt düğmeleri belgenin kendi listesindedir.
 */
function UnpostedCard() {
  const unposted = useUnposted();
  const n = unposted.data;
  const toplam = unpostedTotal(n);

  return (
    <Card className="mt-4">
      <CardHeader
        title="Deftere girmemiş belgeler"
        description="Kayıt ayrı bir adımdır; buradaki sayı sıfır değilse mizan eksiktir."
      />
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        {unposted.isPending ? (
          <Spinner />
        ) : unposted.isError ? (
          <ErrorState message={errorMessage(unposted.error)} />
        ) : toplam === 0 ? (
          <Badge tone="ok">tümü kayıtlı</Badge>
        ) : (
          <>
            <Badge tone={(n?.sales ?? 0) > 0 ? 'warn' : 'neutral'}>satış {n?.sales ?? 0}</Badge>
            <Badge tone={(n?.purchases ?? 0) > 0 ? 'warn' : 'neutral'}>
              alış {n?.purchases ?? 0}
            </Badge>
            <Badge tone={(n?.expenses ?? 0) > 0 ? 'warn' : 'neutral'}>
              gider {n?.expenses ?? 0}
            </Badge>
            <Badge tone={(n?.payments ?? 0) > 0 ? 'warn' : 'neutral'}>
              tahsilat {n?.payments ?? 0}
            </Badge>
            <Badge tone={(n?.vatSettlements ?? 0) > 0 ? 'warn' : 'neutral'}>
              KDV mahsubu {n?.vatSettlements ?? 0}
            </Badge>
            <Badge tone={(n?.vatPayments ?? 0) > 0 ? 'warn' : 'neutral'}>
              KDV ödemesi {n?.vatPayments ?? 0}
            </Badge>
            <span className="text-xs text-[var(--c-ink-soft)]">
              Kayıt düğmesi belgenin kendi listesindedir.
            </span>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * YIL SONU KAPANIŞI.
 *
 * ── ⚠ ÜÇ ADIM, ÜÇ AYRI KARAR ──────────────────────────────────────────────
 * Ne eksik (hazırlık) → ne yazılacak (önizleme) → yaz (kapat). Tek düğmeye
 * indirgenseydi operatör defterine ne yazıldığını ancak yazıldıktan SONRA
 * görürdü.
 *
 * ⚠ ENGEL İLE UYARI AYRI GÖSTERİLİR. Eksik bir defteri kapatmak eksikliği
 * KALICI kılar (engel); verilmemiş bir KDV beyanı ise defterin kapanmasından
 * ayrı bir borçtur (uyarı) ve kapanışı kilitlemesi için sebep yok.
 *
 * ⚠ GERİ AÇMA GİZLENMEZ. Beyan verildikten sonra hata bulmak sıradan bir
 * olaydır; kapanışı tek yönlü göstermek, operatörü ya defteri bozmaya ya da
 * hatayı gizlemeye iterdi. Geri açma kapanış fişini TERS ÇEVİRİR, silmez.
 */
function YearEndCard({ readOnly }: { readOnly: boolean }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [packing, setPacking] = useState(false);

  const readiness = useClosingReadiness(year);
  const preview = useClosingPreview(year, showPreview);
  const close = useCloseYear();
  const reopen = useReopenYear();

  const data = readiness.data;
  const blockers = (data?.issues ?? []).filter((i) => i.blocking);
  const warnings = (data?.issues ?? []).filter((i) => !i.blocking);

  return (
    <Card className="mt-4">
      <CardHeader
        title="Yıl sonu kapanışı"
        description="Sonuç hesapları sıfırlanır, kâr ya da zarar sermayeye taşınır. Kapatılmış yıla kayıt atılamaz."
      />
      <div className="space-y-3 px-4 py-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="year-end-year">Mali yıl</label>
          <select
            id="year-end-year"
            value={year}
            onChange={(e) => {
              setYear(Number(e.target.value));
              setShowPreview(false);
              setError(null);
            }}
            className="rounded-[var(--r-md)] border border-[var(--c-border-strong)] bg-[var(--c-surface)] px-2 py-1"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          {readiness.isPending && <Spinner />}
        </div>

        {readiness.isError && <ErrorState message={errorMessage(readiness.error)} />}

        {data && (
          <>
            <p>
              Durum:{' '}
              <Badge tone={data.status === 'CLOSED' ? 'neutral' : 'ok'}>
                {data.status === 'CLOSED'
                  ? 'kapalı'
                  : data.status === 'NOT_OPENED'
                    ? 'açılmamış'
                    : 'açık'}
              </Badge>{' '}
              · Sonuç: <strong>{money(data.result)}</strong>{' '}
              <span className="text-[var(--c-ink-soft)]">
                (gelir {money(data.income)} − gider {money(data.expense)})
              </span>
            </p>

            {data.closingEntryNumber !== null && (
              <p>
                Kapanış fişi: <strong>{data.closingEntryNumber}</strong>
              </p>
            )}

            {blockers.length > 0 && (
              <ul className="space-y-1">
                {blockers.map((i) => (
                  <li key={i.code} className="flex gap-2">
                    <Badge tone="danger">engel</Badge>
                    <span>
                      {CLOSING_ISSUES[i.code] ?? i.code}
                      {i.count !== undefined && i.count > 0 && ` (${i.count})`}
                      {i.detail !== undefined && (
                        <span className="text-[var(--c-ink-faint)]"> — {i.detail}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {warnings.length > 0 && (
              <ul className="space-y-1">
                {warnings.map((i) => (
                  <li key={i.code} className="flex gap-2">
                    <Badge tone="warn">uyarı</Badge>
                    <span>{CLOSING_ISSUES[i.code] ?? i.code}</span>
                  </li>
                ))}
              </ul>
            )}

            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                {data.status !== 'CLOSED' && (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => setShowPreview((v) => !v)}>
                      {showPreview ? 'Önizlemeyi gizle' : 'Kapanış fişini önizle'}
                    </Button>
                    <Button
                      disabled={!data.closable || close.isPending}
                      onClick={() => {
                        setError(null);
                        close.mutate(year, {
                          onSuccess: () => toast.success(`${year} kapatıldı.`),
                          onError: (e: unknown) => setError(errorMessage(e)),
                        });
                      }}
                    >
                      Yılı kapat
                    </Button>
                  </>
                )}
                {data.status === 'CLOSED' && (
                  <Button
                    variant="ghost"
                    disabled={reopen.isPending}
                    onClick={() => {
                      setError(null);
                      reopen.mutate(year, {
                        onSuccess: () =>
                          toast.success(`${year} geri açıldı; kapanış fişi ters çevrildi.`),
                        onError: (e: unknown) => setError(errorMessage(e)),
                      });
                    }}
                  >
                    Yılı geri aç
                  </Button>
                )}
              </div>
            )}

            {showPreview && preview.isSuccess && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[var(--c-ink-faint)]">
                    <th className="py-1 text-left font-medium">Hesap</th>
                    <th className="py-1 text-left font-medium">Borç</th>
                    <th className="py-1 text-left font-medium">Alacak</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.lines.map((l, i) => (
                    <tr key={`${l.accountCode}-${String(i)}`}>
                      <td className="py-0.5">{l.accountCode}</td>
                      <td className="py-0.5">{l.debit === '0.00' ? '—' : money(l.debit)}</td>
                      <td className="py-0.5">{l.credit === '0.00' ? '—' : money(l.credit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {showPreview && preview.isError && (
              <p className="text-[var(--c-danger)]">{errorMessage(preview.error)}</p>
            )}

            <p className="text-xs text-[var(--c-ink-faint)]">
              ⚠ Kapanış <strong>vergi hesaplamaz</strong>: gelir vergisi ve sosyal primler
              işletmenin değil, kişinin borcudur. Ne kadar ayrılacağını “Ne kadar kenara
              ayırmalıyım?” ekranı söyler.
            </p>

            {/* ⚠ PAKET HER ZAMAN İNDİRİLEBİLİR — kapanmış olsun olmasın.
                Denetim, defterin kapanmasını beklemez. */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={packing}
                onClick={() => {
                  setError(null);
                  setPacking(true);
                  downloadAccountantPackage(year)
                    /* ⚠ Ad sunucudan gelir; başlık okunamazsa yılı taşıyan
                       bir ad yine de verilir — adsız indirme kullanıcıda
                       "dosya.zip" olarak kalırdı. */
                    .then((f) =>
                      saveBlob(f.blob, f.fileName ?? `centramir-${year}-muhasebe-paketi.zip`),
                    )
                    .catch((e: unknown) => setError(errorMessage(e)))
                    .finally(() => setPacking(false));
                }}
              >
                {packing ? 'Paket hazırlanıyor…' : 'Muhasebeci paketini indir (ZIP)'}
              </Button>
              <span className="text-xs text-[var(--c-ink-faint)]">
                Defterler + belgeler + sha256 manifesti
              </span>
            </div>

            {error !== null && <p className="text-[var(--c-danger)]">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * ENVANTER DEFTERİ (inventarisboek).
 *
 * ⚠ BU DEFTER BİR BEYANDIR, bir rapor değil. Çıkarıldığı gün DONAR: sonradan
 * bir belge düzeltilirse yeniden hesaplanan bir "envanter", deftere geçenden
 * farklı çıkardı. Bu yüzden ekranda önce ÖNİZLEME, sonra "çıkar" var — ve
 * çıkarılmış envanter düzeltilmez, İPTAL edilip yenisi çıkarılır.
 *
 * ⚠ KAPSAM DIŞI OLANLAR EKRANDA YAZILI. Boş bir "stok" satırı, sayım yapılmış
 * izlenimi verirdi; yapılmadığını söylemek yapılmış gibi göstermekten iyidir.
 */
function InventoryCard({ readOnly }: { readOnly: boolean }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const list = useInventories();
  const preview = useInventoryPreview(year, showPreview);
  const take = useTakeInventory();

  const taken = (list.data ?? []).find((i) => i.fiscalYear === year && i.status === 'FINAL');
  const content = taken?.content ?? preview.data?.content ?? null;

  return (
    <Card className="mt-4">
      <CardHeader
        title="Envanter defteri"
        description="Basitleştirilmiş rejimin zorunlu defteri: 31 Aralık’ta elde ne var, ne borç var."
      />
      <div className="space-y-3 px-4 py-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="inventory-year">Yıl</label>
          <select
            id="inventory-year"
            value={year}
            onChange={(e) => {
              setYear(Number(e.target.value));
              setShowPreview(false);
              setError(null);
            }}
            className="rounded-[var(--r-md)] border border-[var(--c-border-strong)] bg-[var(--c-surface)] px-2 py-1"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          {taken ? (
            <Badge tone="ok">çıkarıldı · {date(taken.takenOn)}</Badge>
          ) : (
            <Badge tone="warn">çıkarılmadı</Badge>
          )}

          {!readOnly && !taken && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setShowPreview((v) => !v)}>
                {showPreview ? 'Önizlemeyi gizle' : 'Önizle'}
              </Button>
              <Button
                size="sm"
                disabled={take.isPending}
                onClick={() => {
                  setError(null);
                  take.mutate(
                    { fiscalYear: year },
                    {
                      onSuccess: () => toast.success(`${year} envanteri çıkarıldı ve donduruldu.`),
                      onError: (e: unknown) => setError(errorMessage(e)),
                    },
                  );
                }}
              >
                Envanteri çıkar
              </Button>
            </>
          )}
        </div>

        {preview.isError && showPreview && (
          <p className="text-[var(--c-danger)]">{errorMessage(preview.error)}</p>
        )}

        {preview.data && !taken && !preview.data.balanced && (
          <p className="text-[var(--c-danger)]">
            ⚠ Aktif − pasif, öz kaynağa eşit değil. Bu bir <strong>defter arızasıdır</strong>;
            envanter çıkarmadan önce sebebi bulunmalı.
          </p>
        )}

        {(taken ?? preview.data) && (
          <p>
            Aktif <strong>{money((taken ?? preview.data)?.totalAssets)}</strong> · Pasif{' '}
            <strong>{money((taken ?? preview.data)?.totalLiabilities)}</strong> · Öz kaynak{' '}
            <strong>{money((taken ?? preview.data)?.equity)}</strong>
            {content && content.unclosedResult !== '0.00' && (
              <>
                {' '}
                · <span className="text-[var(--c-ink-soft)]">kapanmamış dönem sonucu</span>{' '}
                <strong>{money(content.unclosedResult)}</strong>
              </>
            )}
          </p>
        )}

        {content && (
          <div className="space-y-3">
            {content.fixedAssets.lines.length > 0 && (
              <div>
                <p className="text-xs font-medium text-[var(--c-ink-soft)]">Duran varlıklar</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[var(--c-ink-faint)]">
                      <th className="py-1 text-left font-medium">Varlık</th>
                      <th className="py-1 text-left font-medium">Maliyet</th>
                      <th className="py-1 text-left font-medium">Birikmiş</th>
                      <th className="py-1 text-left font-medium">Net değer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {content.fixedAssets.lines.map((l) => (
                      <tr key={`${l.label}-${l.acquisitionDate}`}>
                        <td className="py-0.5">{l.label}</td>
                        <td className="py-0.5">{money(l.cost)}</td>
                        <td className="py-0.5">{money(l.accumulated)}</td>
                        <td className="py-0.5">{money(l.bookValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <InventoryColumn title="Aktifler" lines={content.ledger.assets} />
              <InventoryColumn title="Pasifler" lines={content.ledger.liabilities} />
            </div>

            {/* ⚠ DEFTERE İŞLENMEMİŞ DEĞERLEME: ayrı durur, toplamlara girmez. */}
            <div className="rounded-[var(--r-md)] bg-[var(--c-surface-alt)] px-3 py-2 text-xs">
              <strong>Devam eden işler (deftere işlenmemiş):</strong>{' '}
              {money(content.workInProgress.value)} · {content.workInProgress.hours} saat ·{' '}
              {content.workInProgress.entries} kayıt
              {content.workInProgress.missingCostRate > 0 && (
                <span className="text-[var(--c-warn)]">
                  {' '}
                  · ⚠ {content.workInProgress.missingCostRate} kaydın maliyet oranı girilmemiş,
                  değerlemeye girmedi
                </span>
              )}
            </div>

            <ul className="space-y-0.5 text-xs text-[var(--c-ink-faint)]">
              {content.exclusions.map((x) => (
                <li key={x}>⚠ {x}</li>
              ))}
            </ul>
          </div>
        )}

        {error !== null && <p className="text-[var(--c-danger)]">{error}</p>}
      </div>
    </Card>
  );
}

function InventoryColumn({ title, lines }: { title: string; lines: InventoryLine[] }) {
  return (
    <div>
      <p className="text-xs font-medium text-[var(--c-ink-soft)]">{title}</p>
      {lines.length === 0 ? (
        <p className="text-xs text-[var(--c-ink-faint)]">—</p>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {lines.map((l) => (
              <tr key={l.reference ?? l.label}>
                <td className="py-0.5">
                  {l.label}
                  {l.reference !== null && (
                    <span className="text-[var(--c-ink-faint)]"> ({l.reference})</span>
                  )}
                </td>
                <td className="py-0.5 text-right tabular-nums">{money(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
